import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../App';
import { api } from '../api';
import { toast } from 'sonner';
import { formatPhone } from '../utils/formatPhone';
import {
  Users, Search, Star, User, FileText, ShieldCheck, Plane,
  Globe, Bed, Briefcase, Heart, CheckCircle, XCircle, X,
  Mail, Send, Building2, Printer, UserMinus, Download, Clock,
  AlertTriangle, ChevronDown, ChevronRight, Phone, Trash2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Popover, PopoverTrigger, PopoverContent } from '../components/ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

import PageLayout from '../components/PageLayout';
import { useSiteSettings } from '../SiteSettingsContext';
import ParticipantProfileModal from '../components/ParticipantProfileModal';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

// Toolbar limited to Bold/Italic/Underline + lists. Matches what a user
// typically wants when pasting from Word without letting fonts/colors leak in.
const GROUP_EMAIL_QUILL_MODULES = {
  toolbar: [
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['clean'],
  ],
};
const GROUP_EMAIL_QUILL_FORMATS = ['bold', 'italic', 'underline', 'list'];

// Strip HTML tags for the plain-text fallback and the empty-content check
// (Quill leaves an empty message as "<p><br></p>").
const stripHtmlToText = (html) => {
  if (!html) return '';
  const div = document.createElement('div');
  div.innerHTML = html;
  // Preserve line breaks between block elements for the text fallback
  div.querySelectorAll('p, li, br, div').forEach(el => {
    el.appendChild(document.createTextNode('\n'));
  });
  return (div.textContent || '').replace(/\n{3,}/g, '\n\n').trim();
};

// ── Role → color mapping ──────────────────────────────────────
const getRoleColor = (workRoles = []) => {
  const r = workRoles.map(s => s.toLowerCase()).join(' ');
  if (/surgeon|surgery|physician|doctor|md|gyn|gynecolog|urogyn|peds|pediatric|podiatr|gastro|gi\b|plastics|hand surg|urolog/.test(r)) return 'orange';
  if (/anesthesi|crna|anesthetist/.test(r)) return 'yellow';
  if (/pacu|recovery/.test(r)) return 'green';
  if (/nurse|tech|scrub|or\b|or tech|or nurse/.test(r)) return 'blue';
  return null;
};

// ── Minor check: under 18 as of a reference date ─────────────
const isMinorAsOf = (dob, asOfDate) => {
  if (!dob) return false;
  const birth = new Date(dob);
  const ref = asOfDate ? new Date(asOfDate) : new Date();
  if (isNaN(birth.getTime()) || isNaN(ref.getTime())) return false;
  let age = ref.getFullYear() - birth.getFullYear();
  const m = ref.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && ref.getDate() < birth.getDate())) age--;
  return age < 18;
};

const ROW_COLORS = {
  orange: 'bg-orange-50 hover:bg-orange-100',
  yellow: 'bg-yellow-50 hover:bg-yellow-100',
  green:  'bg-green-50  hover:bg-green-100',
  blue:   'bg-blue-50   hover:bg-blue-100',
  none:   'bg-white hover:bg-slate-50',
};

const ROLE_FILTER_OPTIONS = [
  { value: 'orange', label: 'Surgeons' },
  { value: 'yellow', label: 'Anesthesia / CRNA' },
  { value: 'blue',   label: 'OR Nurses / Techs' },
  { value: 'green',  label: 'PACU / Recovery' },
  { value: 'none',   label: 'Non-Medical' },
];

// ── Status badge — simplified: Complete (all tasks done) or Unassigned ──
const ParticipantStatusBadge = ({ status, tasks }) => {
  if (status === 'unassigned') {
    return <Badge className="text-xs border px-2 py-0.5 bg-slate-100 text-slate-500 border-slate-200">Unassigned</Badge>;
  }
  // Check if all key tasks are complete
  const t = tasks || {};
  const allDone = t.profile_complete && t.passport_complete && t.waiver_signed && t.flight_submitted && t.hotel_preferences && t.donation_confirmed;
  if (allDone) {
    return <Badge className="text-xs border px-2 py-0.5 bg-green-100 text-green-700 border-green-200">Complete</Badge>;
  }
  // No badge for in-progress participants
  return null;
};

// ── Task icon ─────────────────────────────────────────────────
const TaskIcon = ({ done, icon: Icon, label, warning, warningText }) => (
  <span title={label} className="relative group">
    <Icon className={`w-5 h-5 ${warning ? 'text-amber-500' : done ? 'text-green-500' : 'text-slate-300'}`} />
    <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-0.5 bg-slate-800 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none z-50">
      {label}: {warning ? (warningText || 'Warning') : done ? 'Complete' : 'Incomplete'}
    </span>
  </span>
);

const TASK_DEFS = [
  { key: 'profile_complete',  icon: User,        label: 'Profile' },
  { key: 'passport_complete', icon: Globe,       label: 'Passport' },
  { key: 'waiver_signed',     icon: FileText,    label: 'Waiver' },
  { key: 'license_uploaded',  icon: ShieldCheck, label: 'License' },
  { key: 'flight_submitted',  icon: Plane,       label: 'Flight' },
  { key: 'hotel_preferences', icon: Bed,         label: 'Hotel Prefs' },
  { key: 'donation_confirmed',icon: Heart,       label: 'Donation' },
  { key: 'room_assigned',     icon: Building2,   label: 'Room' },
  { key: 'bag_assigned',      icon: Briefcase,   label: 'Bag' },
];

// ── Sidebar ───────────────────────────────────────────────────

// ── Admin Notes sub-component ─────────────────────────────────
const AdminNotes = ({ participant, onSave }) => {
  const [notes, setNotes] = useState(participant?.profile?.admin_notes || '');
  const [saving, setSaving] = useState(false);

  // Sync when participant changes
  /* eslint-disable react-hooks/exhaustive-deps */
  React.useEffect(() => {
    setNotes(participant?.profile?.admin_notes || '');
  }, [participant?.user_id, participant?.trip_id]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(participant.user_id, notes);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-2">
      <textarea
        value={notes}
        onChange={e => setNotes(e.target.value)}
        placeholder="Internal notes visible only to admins..."
        rows={3}
        className="w-full text-xs border border-slate-200 rounded-lg px-3 py-2 resize-none focus:outline-none focus:ring-1 focus:ring-primary/30 focus:border-primary/50"
        data-testid="admin-notes-input"
      />
      <button
        onClick={handleSave}
        disabled={saving || notes === (participant?.profile?.admin_notes || '')}
        className="text-xs text-primary hover:underline disabled:opacity-40 disabled:cursor-not-allowed"
        data-testid="save-notes-btn"
      >
        {saving ? 'Saving...' : 'Save notes'}
      </button>
    </div>
  );
};

// ── Detail panel ──────────────────────────────────────────────
const DetailPanel = ({ participant, trips, onClose, onInvite, onDonationToggle, onNoBagToggle, onAssignResponsibilities, onSaveNotes, onViewProfile, onUnassign, onGuardianAction, currentUser, onSaved }) => {
  const [inviteTrip, setInviteTrip] = useState(participant?.trip_id || '');
  const [sending, setSending] = useState(false);
  // Local state for admin controls so they persist after save without full page reload
  const [elevatedAccess, setElevatedAccess] = useState(!!(participant?.user?.elevated_access || participant?.elevated_access));
  const [personType, setPersonType] = useState(participant?.user?.user_type || 'participant');

  // Admin inline edit mode — unlocks the panel fields for admin overrides
  const isAdmin = currentUser?.role === 'admin';
  const [adminEdit, setAdminEdit] = useState(false);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  // Sync when participant changes (e.g. detail panel switches to different person)
  /* eslint-disable react-hooks/exhaustive-deps */
  React.useEffect(() => {
    setElevatedAccess(!!(participant?.user?.elevated_access || participant?.elevated_access));
    setPersonType(participant?.user?.user_type || 'participant');
    // Exit edit mode when switching participants
    setAdminEdit(false);
  }, [participant?.user_id]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const startEdit = () => {
    const p = participant?.profile || {};
    setDraft({
      email: participant?.user?.email || '',
      phone: p.phone || '',
      street_address: p.street_address || '',
      city: p.city || '',
      state: p.state || '',
      zip_code: p.zip_code || '',
      organization: p.organization || '',
      first_name: p.first_name || '',
      last_name: p.last_name || '',
    });
    setAdminEdit(true);
  };

  const cancelEdit = () => { setAdminEdit(false); setDraft({}); };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const originalEmail = participant?.user?.email || '';
      const emailChanged = draft.email && draft.email.trim().toLowerCase() !== originalEmail.toLowerCase();
      // 1) Email change FIRST — if this fails (invalid, duplicate) we abort
      //    before touching the profile so we never end up with an inconsistent
      //    partial save.
      if (emailChanged) {
        await api.patch(`/admin/participants/${participant.user_id}/email`, { new_email: draft.email.trim().toLowerCase() });
      }
      // 2) Profile fields
      const profilePayload = {
        first_name: draft.first_name,
        last_name: draft.last_name,
        phone: draft.phone,
        street_address: draft.street_address,
        city: draft.city,
        state: draft.state,
        zip_code: draft.zip_code,
        organization: draft.organization,
      };
      await api.put(`/profile?on_behalf_of=${participant.user_id}`, profilePayload);
      toast.success(emailChanged
        ? 'Profile & email updated. Their active sessions were signed out.'
        : 'Profile updated');
      setAdminEdit(false);
      setDraft({});
      onSaved && onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save changes');
    } finally { setSaving(false); }
  };

  if (!participant) return null;
  const { user, profile, trip, tasks, participant_status, team_designation } = participant;
  const name = profile?.first_name
    ? `${profile.first_name} ${profile.last_name || ''}`.trim()
    : user?.name || 'Unknown';
  const roles = profile?.work_roles || [];
  // Gold star: only from responsibilities (trip_role is for access control only)
  const isLeader = (participant.responsibilities || []).some(r => r.toLowerCase().includes('leader'));
  // Minor check against trip start dates
  const detailTrips = (participant.allAssignments || [participant]).map(a => a.trip).filter(Boolean);
  const detailTripStart = detailTrips.map(t => t?.start_date).filter(Boolean).sort()[0] || null;
  const isMinor = isMinorAsOf(profile?.date_of_birth, detailTripStart);

  const handleInvite = async () => {
    setSending(true);
    try {
      await onInvite(inviteTrip || participant.trip_id, participant.user_id);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed top-0 right-0 h-full w-full sm:w-[480px] bg-white border-l border-slate-200 shadow-2xl z-40 overflow-y-auto">
      <div className="sticky top-0 bg-white border-b border-slate-100 px-5 py-4 flex items-center justify-between">
        <h2 className="font-heading font-bold text-slate-800">Participant Details</h2>
        <div className="flex items-center gap-1">
          {isAdmin && !adminEdit && (
            <button
              onClick={startEdit}
              className="text-xs text-amber-700 hover:underline px-2 py-1 rounded-lg hover:bg-amber-50 transition-colors"
              data-testid="admin-edit-btn"
            >
              Edit Profile
            </button>
          )}
          {isAdmin && participant?.trip_id && (
            <a
              href={`/flight-hotel?edit_user=${participant.user_id}&trip_id=${participant.trip_id}`}
              className="text-xs text-primary hover:underline px-2 py-1 rounded-lg hover:bg-primary/5 transition-colors"
              data-testid="admin-edit-flight-hotel-btn"
            >
              Edit Flight & Hotel
            </a>
          )}
          <button
            onClick={() => onViewProfile && onViewProfile(participant)}
            className="text-xs text-primary hover:underline px-2 py-1 rounded-lg hover:bg-primary/5 transition-colors"
            data-testid="view-full-profile-btn"
          >
            View Full Profile
          </button>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-slate-100"><X className="w-5 h-5 text-slate-400" /></button>
        </div>
      </div>

      {adminEdit && (
        <div className="bg-amber-50 border-b border-amber-200 px-5 py-2.5 flex items-center justify-between" data-testid="admin-edit-banner">
          <div className="text-xs text-amber-800">
            <strong>Admin edit mode:</strong> editing <span className="font-semibold">{name}</span>
          </div>
          <div className="flex gap-1.5">
            <button onClick={cancelEdit} disabled={saving} className="text-xs px-3 py-1 rounded-full border border-amber-300 text-amber-800 hover:bg-amber-100 disabled:opacity-40" data-testid="admin-edit-cancel">Cancel</button>
            <button onClick={saveEdit} disabled={saving} className="text-xs px-3 py-1 rounded-full bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-40" data-testid="admin-edit-save">{saving ? 'Saving…' : 'Save'}</button>
          </div>
        </div>
      )}

      <div className="p-5 space-y-5">
        {/* Identity */}
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
            <User className="w-6 h-6 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              {isLeader && <Star className="w-4 h-4 text-amber-400 fill-amber-400" />}
              {adminEdit ? (
                <div className="flex gap-1.5 flex-1">
                  <input value={draft.first_name} onChange={e => setDraft({ ...draft, first_name: e.target.value })} placeholder="First name" className="flex-1 min-w-0 text-sm font-semibold px-2 py-1 border border-amber-300 rounded" data-testid="admin-edit-first-name" />
                  <input value={draft.last_name} onChange={e => setDraft({ ...draft, last_name: e.target.value })} placeholder="Last name" className="flex-1 min-w-0 text-sm font-semibold px-2 py-1 border border-amber-300 rounded" data-testid="admin-edit-last-name" />
                </div>
              ) : (
                <p className="font-semibold text-slate-800">{name}</p>
              )}
              {isMinor && <Badge className="bg-purple-100 text-purple-700 border-0 text-xs" data-testid="detail-minor-badge">Minor</Badge>}
            </div>
            {adminEdit ? (
              <div className="space-y-1.5 mt-2">
                <div>
                  <label className="text-[10px] uppercase text-slate-500 tracking-wide">Email</label>
                  <input type="email" value={draft.email} onChange={e => setDraft({ ...draft, email: e.target.value })} className="w-full text-sm px-2 py-1 border border-amber-300 rounded" data-testid="admin-edit-email" />
                </div>
                <div>
                  <label className="text-[10px] uppercase text-slate-500 tracking-wide">Phone</label>
                  <input value={draft.phone} onChange={e => setDraft({ ...draft, phone: e.target.value })} placeholder="555-123-4567" className="w-full text-sm px-2 py-1 border border-amber-300 rounded" data-testid="admin-edit-phone" />
                </div>
                <div>
                  <label className="text-[10px] uppercase text-slate-500 tracking-wide">Street</label>
                  <input value={draft.street_address} onChange={e => setDraft({ ...draft, street_address: e.target.value })} className="w-full text-sm px-2 py-1 border border-amber-300 rounded" data-testid="admin-edit-street" />
                </div>
                <div className="grid grid-cols-3 gap-1.5">
                  <div>
                    <label className="text-[10px] uppercase text-slate-500 tracking-wide">City</label>
                    <input value={draft.city} onChange={e => setDraft({ ...draft, city: e.target.value })} className="w-full text-sm px-2 py-1 border border-amber-300 rounded" data-testid="admin-edit-city" />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase text-slate-500 tracking-wide">State</label>
                    <input value={draft.state} onChange={e => setDraft({ ...draft, state: e.target.value })} maxLength={2} className="w-full text-sm px-2 py-1 border border-amber-300 rounded uppercase" data-testid="admin-edit-state" />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase text-slate-500 tracking-wide">Zip</label>
                    <input value={draft.zip_code} onChange={e => setDraft({ ...draft, zip_code: e.target.value })} className="w-full text-sm px-2 py-1 border border-amber-300 rounded" data-testid="admin-edit-zip" />
                  </div>
                </div>
                <div>
                  <label className="text-[10px] uppercase text-slate-500 tracking-wide">Organization</label>
                  <input value={draft.organization} onChange={e => setDraft({ ...draft, organization: e.target.value })} className="w-full text-sm px-2 py-1 border border-amber-300 rounded" data-testid="admin-edit-org" />
                </div>
              </div>
            ) : (
              <>
                <p className="text-sm text-slate-500">{user?.email}</p>
                <div className="text-xs mt-1 space-y-0.5">
                  {profile?.phone
                    ? <p className="text-slate-500">{formatPhone(profile.phone)}</p>
                    : <p className="text-red-500">Phone: <span className="italic">missing</span></p>}
                  {profile?.street_address
                    ? <p className="text-slate-500">{profile.street_address}</p>
                    : <p className="text-red-500">Street address: <span className="italic">missing</span></p>}
                  <p className="text-slate-500">
                    {profile?.city
                      ? profile.city
                      : <span className="text-red-500 italic">city missing</span>}
                    {', '}
                    {profile?.state
                      ? profile.state
                      : <span className="text-red-500 italic">state missing</span>}
                    {' '}
                    {profile?.zip_code
                      ? profile.zip_code
                      : <span className="text-red-500 italic">zip missing</span>}
                  </p>
                  {profile?.organization
                    ? <p className="text-slate-400">{profile.organization}</p>
                    : <p className="text-red-500">Organization: <span className="italic">missing</span></p>}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Roles + Excursion */}
        {roles.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Medical Specialty</p>
            <div className="flex flex-wrap gap-1.5">
              {roles.map(r => <Badge key={r} variant="outline" className="text-xs">{r}</Badge>)}
              {profile?.excursion_interest && (
                <Badge className="bg-green-100 text-green-700 border-0 text-xs">Excursion ✓</Badge>
              )}
            </div>
          </div>
        )}

        {/* Responsibilities */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Trip Responsibilities</p>
            <button
              onClick={() => onAssignResponsibilities && onAssignResponsibilities(participant)}
              className="text-xs text-primary hover:underline"
              data-testid="assign-responsibilities-btn"
            >
              {(participant.responsibilities?.length > 0) ? 'Edit' : '+ Assign'}
            </button>
          </div>
          {participant.responsibilities?.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {participant.responsibilities.map(r => (
                <span key={r} className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">{r}</span>
              ))}
            </div>
          ) : (
            <p className="text-xs text-slate-400 italic">No responsibilities assigned</p>
          )}
        </div>

        {/* Trip(s) + status — show all assignments */}
        {(participant.allAssignments || [participant]).map((assign, ai) => {
          const assignTrip = assign.trip;
          return (
            <div key={assign.trip_id || ai} className="bg-slate-50 rounded-xl p-4 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-500 font-semibold uppercase tracking-wide">
                  {(participant.allAssignments?.length || 1) > 1 ? `Trip ${ai + 1}` : 'Trip'}
                </span>
                <span className="text-sm font-medium text-slate-800 text-right max-w-[180px]">
                  {assignTrip?.name || assign.trip_id}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-500">Status</span>
                <ParticipantStatusBadge status={assign.participant_status} tasks={participant.tasks} />
              </div>
              {assign.requested_at && (
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500">Requested</span>
                  <span className="text-xs text-slate-600">{new Date(assign.requested_at).toLocaleDateString()}</span>
                </div>
              )}
              {assign.invitation_sent_at && (
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500">Invited</span>
                  <span className="text-xs text-slate-600">{new Date(assign.invitation_sent_at).toLocaleDateString()}</span>
                </div>
              )}
              <Button
                variant="ghost"
                size="sm"
                className="w-full mt-2 text-xs text-red-500 hover:text-red-700 hover:bg-red-50 h-7"
                onClick={() => onUnassign && onUnassign(assign.trip_id, participant.user_id, assignTrip?.name)}
                data-testid={`unassign-trip-${assign.trip_id}`}
              >
                <UserMinus className="w-3.5 h-3.5 mr-1" />
                Unassign from Trip
              </Button>
            </div>
          );
        })}

        {/* Task checklist */}
        <div>
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Task Completion</p>
          <div className="space-y-2">
            {TASK_DEFS.map(({ key, icon: Icon, label }) => (
              <div key={key} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Icon className={`w-4 h-4 ${tasks?.[key] ? 'text-green-500' : 'text-slate-300'}`} />
                  <span className="text-sm text-slate-700">{label}</span>
                </div>
                {key === 'donation_confirmed' ? (
                  <div className="flex items-center gap-2">
                    <span className={`text-xs ${tasks?.donation_confirmed ? 'text-green-600' : 'text-slate-400'}`}>
                      {tasks?.donation_confirmed ? 'Confirmed' : 'Not confirmed'}
                    </span>
                    <Checkbox
                      checked={tasks?.donation_confirmed || false}
                      onCheckedChange={(v) => onDonationToggle(participant.trip_id || participant.allAssignments?.[0]?.trip_id, participant.user_id, v)}
                      data-testid="donation-toggle"
                    />
                  </div>
                ) : key === 'bag_assigned' ? (
                  <div className="flex items-center gap-2">
                    <span className={`text-xs ${tasks?.bag_assigned ? 'text-green-600' : 'text-slate-400'}`}>
                      {tasks?.bag_info?.bag_number === 'None' ? 'No bag' : tasks?.bag_info?.bag_number || (tasks?.bag_assigned ? 'Assigned' : 'Pending')}
                    </span>
                    {!tasks?.bag_assigned && (
                      <button
                        onClick={() => onNoBagToggle && onNoBagToggle(participant.trip_id || participant.allAssignments?.[0]?.trip_id, participant.user_id, true)}
                        className="text-xs text-slate-400 hover:text-primary underline"
                        data-testid="no-bag-toggle"
                      >
                        No bag
                      </button>
                    )}
                    {tasks?.no_bag_needed && (
                      <button
                        onClick={() => onNoBagToggle && onNoBagToggle(participant.trip_id || participant.allAssignments?.[0]?.trip_id, participant.user_id, false)}
                        className="text-xs text-slate-400 hover:text-primary underline"
                      >
                        Undo
                      </button>
                    )}
                  </div>
                ) : (
                  tasks?.[key]
                    ? <CheckCircle className="w-4 h-4 text-green-500" />
                    : <XCircle className="w-4 h-4 text-slate-300" />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Admin Notes */}
        <div className="border-t border-slate-100 pt-4">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Admin Notes</p>
          <AdminNotes participant={participant} onSave={onSaveNotes} />
        </div>

        {/* Guardian Link */}
        <div className="border-t border-slate-100 pt-4">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Guardian / Dependent</p>
          {participant.guardian_name ? (
            <div className="flex items-center justify-between">
              <p className="text-sm text-slate-700">Guardian: <span className="font-medium">{participant.guardian_name}</span></p>
              <button onClick={() => onGuardianAction && onGuardianAction('unlink', participant)} className="text-xs text-red-500 hover:underline">Remove</button>
            </div>
          ) : participant.dependent_name ? (
            <div className="flex items-center justify-between">
              <p className="text-sm text-slate-700">Dependent: <span className="font-medium">{participant.dependent_name}</span></p>
              <button onClick={() => onGuardianAction && onGuardianAction('unlink', participant)} className="text-xs text-red-500 hover:underline">Remove</button>
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-xs text-slate-400 italic">No guardian link</p>
              <Button size="sm" variant="outline" className="h-7 text-xs rounded-full"
                onClick={() => onGuardianAction && onGuardianAction('set-guardian', participant)}
                data-testid="set-guardian-btn">Set Guardian</Button>
            </div>
          )}
        </div>

        {/* Admin-only controls */}
        <div className="border-t border-slate-100 pt-4 space-y-3">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Admin Controls</p>

          {/* Person Type */}
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-700">Person Type</p>
              <p className="text-xs text-slate-400">Controls roster + task checklist</p>
            </div>
            <select
              value={personType}
              onChange={async (e) => {
                const val = e.target.value;
                setPersonType(val);
                try {
                  await api.put(`/users/${participant.user_id}/user-type`, { user_type: val });
                  toast.success('Person type updated');
                } catch { toast.error('Failed to update'); setPersonType(personType); }
              }}
              className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white text-slate-700"
              data-testid="person-type-select"
            >
              <option value="participant">Trip Participant</option>
              <option value="staff">Support Staff</option>
            </select>
          </div>

          {/* Elevated permissions access */}
          <div
            className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-colors ${
              elevatedAccess
                ? 'border-primary/40 bg-primary/5'
                : 'border-slate-200 bg-white hover:border-slate-300'
            }`}
            onClick={async () => {
              const newVal = !elevatedAccess;
              setElevatedAccess(newVal);
              try {
                await api.put(`/users/${participant.user_id}/elevated-access`, { elevated_access: newVal });
                toast.success(newVal
                  ? `${participant.profile?.first_name || 'User'} added to Permissions page`
                  : `${participant.profile?.first_name || 'User'} removed from Permissions page`
                );
              } catch {
                toast.error('Failed to update');
                setElevatedAccess(!newVal); // revert on error
              }
            }}
            data-testid="elevated-access-toggle"
          >
            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors ${
              elevatedAccess ? 'bg-primary border-primary' : 'border-slate-300'
            }`}>
              {elevatedAccess && (
                <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              )}
            </div>
            <div className="flex-1">
              <p className="text-sm text-slate-700 font-medium">Grant permissions access</p>
              <p className="text-xs text-slate-400">
                {elevatedAccess
                  ? 'Included on Permissions page — can be given elevated access'
                  : 'Not on Permissions page — standard access only'}
              </p>
            </div>
          </div>

          {/* Send password reset */}
          {participant.user?.email && (
            <button
              onClick={async () => {
                try {
                  await api.post('/auth/forgot-password', { email: participant.user.email });
                  toast.success(`Password reset email sent to ${participant.user.email}`);
                } catch { toast.error('Failed to send reset email'); }
              }}
              className="text-xs text-primary hover:underline flex items-center gap-1"
              data-testid="admin-send-reset-btn"
            >
              Send password reset email
            </button>
          )}
        </div>

        {/* Invite section */}
        <div className="border-t border-slate-100 pt-4">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Send Invitation</p>
          <Select value={inviteTrip} onValueChange={setInviteTrip}>
            <SelectTrigger className="mb-3" data-testid="invite-trip-select">
              <SelectValue placeholder="Select trip to invite to..." />
            </SelectTrigger>
            <SelectContent>
              {trips.map(t => (
                <SelectItem key={t.trip_id} value={t.trip_id}>{t.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            className="w-full rounded-full bg-primary"
            disabled={!inviteTrip || sending}
            onClick={handleInvite}
            data-testid="send-invite-btn"
          >
            <Send className="w-4 h-4 mr-2" />
            {sending ? 'Sending...' : 'Send Invitation Email'}
          </Button>
          <p className="text-xs text-slate-400 mt-2 text-center">Sends via Gmail to {user?.email}</p>
        </div>
      </div>
    </div>
  );
};

// ── Prospect card ─────────────────────────────────────────────
const ProspectCard = ({ prospect, onStatusUpdate, onSendTripInvite, onDelete, trips }) => {
  const { declineEmailA, declineEmailB } = useSiteSettings();
  const [showDeclineDialog, setShowDeclineDialog] = useState(false);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [selectedTripId, setSelectedTripId] = useState('');
  const [previewType, setPreviewType] = useState(null);
  const [sendingInvite, setSendingInvite] = useState(false);

  const handleDecline = async (emailType) => {
    await onStatusUpdate(prospect.prospect_id, 'declined', emailType);
    setShowDeclineDialog(false);
  };

  const handleSendTripInvite = async () => {
    if (!selectedTripId || !prospect.approved_user_id) return;
    setSendingInvite(true);
    try {
      await onSendTripInvite(prospect.approved_user_id, selectedTripId);
      setShowInviteDialog(false);
    } finally {
      setSendingInvite(false);
    }
  };

  const previewBody = (template) =>
    template.body.replace('{name}', prospect.name || 'Volunteer');

  return (
    <>
      <div className="flex items-start justify-between p-4 bg-white rounded-xl border border-slate-100 hover:shadow-sm transition-shadow" data-testid={`prospect-${prospect.prospect_id}`}>
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center">
            <User className="w-4 h-4 text-slate-400" />
          </div>
          <div>
            <p className="font-medium text-slate-800 text-sm">{prospect.name}</p>
            <p className="text-xs text-slate-500">{prospect.email} · {prospect.work_role}</p>
            {prospect.service_area && <p className="text-xs text-slate-400">{prospect.service_area}</p>}
            {prospect.status === 'approved' && prospect.approved_user_id && (
              <p className="text-xs text-green-600 mt-0.5">Account created — awaiting trip invitation</p>
            )}
            {prospect.status === 'invited' && (
              <p className="text-xs text-blue-600 mt-0.5">Trip invitation sent — awaiting response</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Badge className={
            prospect.status === 'approved' ? 'bg-green-100 text-green-700 border-0' :
            prospect.status === 'invited' ? 'bg-blue-100 text-blue-700 border-0' :
            prospect.status === 'declined' ? 'bg-red-100 text-red-700 border-0' :
            'bg-yellow-100 text-yellow-700 border-0'
          }>{prospect.status || 'new'}</Badge>

          {prospect.status === 'new' && (
            <>
              <Button size="sm" variant="outline" className="h-7 text-xs border-green-300 text-green-700 hover:bg-green-50"
                onClick={() => onStatusUpdate(prospect.prospect_id, 'approved')} data-testid={`approve-${prospect.prospect_id}`}>
                Approve
              </Button>
              <Button size="sm" variant="outline" className="h-7 text-xs border-red-300 text-red-600 hover:bg-red-50"
                onClick={() => setShowDeclineDialog(true)} data-testid={`decline-${prospect.prospect_id}`}>
                Decline
              </Button>
            </>
          )}

          {prospect.status === 'approved' && prospect.approved_user_id && (
            <Button size="sm" className="h-7 text-xs bg-primary rounded-full"
              onClick={() => setShowInviteDialog(true)} data-testid={`send-trip-invite-${prospect.prospect_id}`}>
              <Send className="w-3 h-3 mr-1.5" />Send Trip Invitation
            </Button>
          )}

          <button
            onClick={() => onDelete && onDelete(prospect.prospect_id, prospect.name)}
            className="text-slate-300 hover:text-red-500 transition-colors"
            title="Delete prospect"
            data-testid={`delete-prospect-${prospect.prospect_id}`}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Send Trip Invitation dialog */}
      <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Send Trip Invitation to {prospect.name}</DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-4">
            <p className="text-sm text-slate-500">
              Select the mission week to invite <strong>{prospect.name}</strong> to.
              A formal invitation email will be sent with their task checklist.
            </p>
            <div className="bg-green-50 border border-green-200 rounded-xl p-3">
              <p className="text-xs text-green-700">
                <strong>Account already created.</strong> {prospect.name} has been sent an account setup email.
                This invitation will assign them to the selected trip.
              </p>
            </div>
            <Select value={selectedTripId} onValueChange={setSelectedTripId}>
              <SelectTrigger data-testid="trip-invite-select">
                <SelectValue placeholder="Select a trip..." />
              </SelectTrigger>
              <SelectContent>
                {(trips || []).map(t => (
                  <SelectItem key={t.trip_id} value={t.trip_id}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInviteDialog(false)}>Cancel</Button>
            <Button
              className="bg-primary"
              disabled={!selectedTripId || sendingInvite}
              onClick={handleSendTripInvite}
              data-testid="confirm-trip-invite-btn"
            >
              <Send className="w-4 h-4 mr-2" />
              {sendingInvite ? 'Sending...' : 'Send Invitation'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Decline dialog */}
      <Dialog open={showDeclineDialog} onOpenChange={setShowDeclineDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Decline {prospect.name}?</DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-3">
            <p className="text-sm text-slate-500">Choose how to handle this decline:</p>

            {/* Option A */}
            <button
              onClick={() => setPreviewType(previewType === 'A' ? null : 'A')}
              className={`w-full text-left p-3 rounded-xl border-2 transition-colors ${previewType === 'A' ? 'border-blue-400 bg-blue-50' : 'border-slate-200 hover:border-blue-200'}`}
              data-testid="decline-option-a"
            >
              <div className="flex items-center justify-between">
                <div>
                  <span className="bg-blue-100 text-blue-700 text-xs font-semibold px-2 py-0.5 rounded-full mr-2">A</span>
                  <span className="text-sm font-medium text-slate-800">Call Back Later</span>
                </div>
                <span className="text-xs text-slate-400">{previewType === 'A' ? 'Hide preview' : 'Preview'}</span>
              </div>
              <p className="text-xs text-slate-500 mt-1 ml-8">Sends encouraging email — we may reach out again</p>
              {previewType === 'A' && (
                <div className="mt-3 ml-8 bg-white border border-blue-100 rounded-lg p-3 text-left">
                  <p className="text-xs font-medium text-slate-600 mb-1">Subject: {declineEmailA.subject}</p>
                  <pre className="text-xs text-slate-600 whitespace-pre-wrap font-sans">{previewBody(declineEmailA)}</pre>
                </div>
              )}
            </button>

            {/* Option B */}
            <button
              onClick={() => setPreviewType(previewType === 'B' ? null : 'B')}
              className={`w-full text-left p-3 rounded-xl border-2 transition-colors ${previewType === 'B' ? 'border-slate-400 bg-slate-50' : 'border-slate-200 hover:border-slate-300'}`}
              data-testid="decline-option-b"
            >
              <div className="flex items-center justify-between">
                <div>
                  <span className="bg-slate-100 text-slate-600 text-xs font-semibold px-2 py-0.5 rounded-full mr-2">B</span>
                  <span className="text-sm font-medium text-slate-800">Polite Breakup</span>
                </div>
                <span className="text-xs text-slate-400">{previewType === 'B' ? 'Hide preview' : 'Preview'}</span>
              </div>
              <p className="text-xs text-slate-500 mt-1 ml-8">Sends respectful final decline email</p>
              {previewType === 'B' && (
                <div className="mt-3 ml-8 bg-white border border-slate-100 rounded-lg p-3 text-left">
                  <p className="text-xs font-medium text-slate-600 mb-1">Subject: {declineEmailB.subject}</p>
                  <pre className="text-xs text-slate-600 whitespace-pre-wrap font-sans">{previewBody(declineEmailB)}</pre>
                </div>
              )}
            </button>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2 pt-2">
            <Button variant="ghost" size="sm" className="text-slate-400 order-last sm:order-first"
              onClick={() => { setShowDeclineDialog(false); setPreviewType(null); }}>
              Cancel
            </Button>
            <div className="flex gap-2 ml-auto">
              <Button size="sm" variant="outline" className="text-xs h-8 border-slate-300"
                onClick={() => handleDecline(null)} data-testid="decline-no-email">
                Decline, no email
              </Button>
              <Button size="sm" variant="outline" className="text-xs h-8 border-blue-300 text-blue-700 hover:bg-blue-50"
                onClick={() => handleDecline('A')} data-testid="decline-send-a">
                Decline + Send A
              </Button>
              <Button size="sm" className="text-xs h-8 bg-slate-700 hover:bg-slate-800"
                onClick={() => handleDecline('B')} data-testid="decline-send-b">
                Decline + Send B
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

// ════════════════════════════════════════════════════════════
// Main Component
// ════════════════════════════════════════════════════════════
const ParticipantsManagement = () => {
  const { logout, user: authUser } = useAuth();
  const [roster, setRoster] = useState([]);
  const [allRoster, setAllRoster] = useState([]);
  const [prospects, setProspects] = useState([]);
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('current');

  // Filters
  const [filterTrip, setFilterTrip] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all'); // keeping for backwards compat but hiding UI
  const [sortPrimary, setSortPrimary] = useState('none');
  const [sortSecondary, setSortSecondary] = useState('name-az');
  const [filterRoles, setFilterRoles] = useState([]);
  const [filterStates, setFilterStates] = useState([]);
  const [search, setSearch] = useState('');

  // Group email selection
  const [selectedEmails, setSelectedEmails] = useState(new Set());
  const [showGroupEmail, setShowGroupEmail] = useState(false);
  const [groupEmailForm, setGroupEmailForm] = useState({ subject: '', message: '', cc: '' });
  const [sendingGroupEmail, setSendingGroupEmail] = useState(false);
  const [adminUsers, setAdminUsers] = useState([]);

  // Detail panel
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [viewingProfile, setViewingProfile] = useState(null);

  // Responsibilities assignment modal
  const [respModalParticipant, setRespModalParticipant] = useState(null);
  const [respTypes, setRespTypes] = useState([]);
  const [selectedResps, setSelectedResps] = useState([]);
  const [savingResps, setSavingResps] = useState(false);

  // Add Participant dialog
  const [showAddDialog, setShowAddDialog] = useState(false);

  // Guardian link management
  const [guardianDialog, setGuardianDialog] = useState(null); // { type: 'set-guardian'|'set-dependent'|'unlink', participant }
  const [guardianLinks, setGuardianLinks] = useState([]);
  const [addForm, setAddForm] = useState({ name: '', email: '', trip_id: '', role: 'participant' });
  const [addSending, setAddSending] = useState(false);

  // Batch Invite dialog
  const [showBatchInvite, setShowBatchInvite] = useState(false);
  const [batchTripId, setBatchTripId] = useState('');
  const [batchSelected, setBatchSelected] = useState(new Set());
  const [batchSearch, setBatchSearch] = useState('');
  const [batchNewEmail, setBatchNewEmail] = useState('');
  const [batchNewName, setBatchNewName] = useState('');
  const [batchSending, setBatchSending] = useState(false);

  // Trip Interest Requests
  const [tripInterests, setTripInterests] = useState([]);
  const [interestYear, setInterestYear] = useState(new Date().getFullYear());

  // Pending Invitations
  const [pendingInvites, setPendingInvites] = useState([]);
  const [pendingInvitesOpen, setPendingInvitesOpen] = useState(true);
  const [resendingId, setResendingId] = useState(null);
  const [nudgingAll, setNudgingAll] = useState(false);
  const [pendingSort, setPendingSort] = useState('name'); // 'name' | 'date'
  const [pendingTripFilter, setPendingTripFilter] = useState('all');

  // Compose email modal
  const [composeEmail, setComposeEmail] = useState(null);
  const [emailForm, setEmailForm] = useState({ subject: '', message: '' });
  const [sendingEmail, setSendingEmail] = useState(false);

  const handleSendEmail = async () => {
    if (!emailForm.subject.trim() || !emailForm.message.trim()) {
      toast.error('Subject and message are required');
      return;
    }
    setSendingEmail(true);
    try {
      await api.post('/participants/send-email', {
        to_email: composeEmail.to_email,
        subject: emailForm.subject,
        message: emailForm.message,
      });
      toast.success(`Email sent to ${composeEmail.name || composeEmail.to_email}`);
      setComposeEmail(null);
      setEmailForm({ subject: '', message: '' });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  const toggleSelectParticipant = (userId) => {
    setSelectedEmails(prev => {
      const next = new Set(prev);
      next.has(userId) ? next.delete(userId) : next.add(userId);
      return next;
    });
  };

  const handleEmailGroupClick = () => {
    if (selectedEmails.size === 0) {
      toast.info('Check the boxes next to participants below to select recipients first');
      return;
    }
    setShowGroupEmail(true);
  };

  const addCcSuggestion = (email) => {
    setGroupEmailForm(f => {
      const existing = f.cc.split(/[,;]/).map(s => s.trim()).filter(Boolean);
      if (existing.includes(email)) return f;
      return { ...f, cc: [...existing, email].join(', ') };
    });
  };

  const handleSendGroupEmail = async () => {
    const messageHtml = groupEmailForm.message;
    const messageText = stripHtmlToText(messageHtml);
    if (!groupEmailForm.subject.trim() || !messageText) {
      toast.error('Subject and message are required');
      return;
    }
    const toEmails = selectedGroupRecipients.map(r => r.email);
    if (toEmails.length === 0) {
      toast.error('No valid recipient emails selected');
      return;
    }
    const ccEmails = groupEmailForm.cc.split(/[,;]/).map(s => s.trim()).filter(s => s.includes('@'));
    setSendingGroupEmail(true);
    try {
      await api.post('/participants/send-group-email', {
        to_emails: toEmails,
        cc_emails: ccEmails,
        subject: groupEmailForm.subject,
        message: messageText,
        message_html: messageHtml,
      });
      toast.success(`Group email sending to ${toEmails.length} participant${toEmails.length !== 1 ? 's' : ''}`);
      setShowGroupEmail(false);
      setSelectedEmails(new Set());
      setGroupEmailForm({ subject: '', message: '', cc: '' });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send group email');
    } finally {
      setSendingGroupEmail(false);
    }
  };

  const currentYear = new Date().getFullYear();

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [currRes, allRes, prospectsRes, tripsRes, respRes, interestsRes, pendingRes, gLinksRes, usersRes] = await Promise.all([
        api.get(`/participants/roster?year=${currentYear}`),
        api.get('/participants/roster?all_years=true'),
        api.get('/prospects'),
        api.get('/trips'),
        api.get('/responsibility-types'),
        api.get('/trip-interests'),
        api.get('/invites/pending'),
        api.get('/guardian-links').catch(() => ({ data: [] })),
        api.get('/users').catch(() => ({ data: [] })),
      ]);
      setRoster(currRes.data);
      setAllRoster(allRes.data);
      setProspects(prospectsRes.data);
      setTrips(tripsRes.data);
      setRespTypes(respRes.data);
      setTripInterests(interestsRes.data || []);
      setPendingInvites(pendingRes.data || []);
      setGuardianLinks(gLinksRes.data || []);
      setAdminUsers(usersRes.data || []);
    } catch {
      toast.error('Failed to load participants');
    } finally {
      setLoading(false);
    }
  }, [currentYear]);

  const handleOpenRespModal = (participant) => {
    setRespModalParticipant(participant);
    setSelectedResps(participant.responsibilities || []);
  };

  const handleSaveResponsibilities = async () => {
    if (!respModalParticipant) return;
    setSavingResps(true);
    try {
      await api.put(`/trips/${respModalParticipant.trip_id}/participants/${respModalParticipant.user_id}/responsibilities`,
        { responsibilities: selectedResps });
      toast.success('Responsibilities saved');
      setRespModalParticipant(null);
      // Refresh roster then update detail panel with fresh data
      const [currRes, allRes] = await Promise.all([
        api.get(`/participants/roster?year=${currentYear}`),
        api.get('/participants/roster?all_years=true'),
      ]);
      setRoster(currRes.data);
      setAllRoster(allRes.data);
      // Update selected participant from refreshed data so detail panel shows new responsibilities
      if (selectedParticipant) {
        const refreshed = [...currRes.data, ...allRes.data].find(
          p => p.user_id === respModalParticipant.user_id && p.trip_id === respModalParticipant.trip_id
        );
        if (refreshed) setSelectedParticipant(refreshed);
      }
    } catch { toast.error('Failed to save'); }
    finally { setSavingResps(false); }
  };

  const toggleResp = (name) =>
    setSelectedResps(prev => prev.includes(name) ? prev.filter(r => r !== name) : [...prev, name]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleInvite = async (tripId, userId) => {
    try {
      await api.post(`/trips/${tripId}/participants/${userId}/invite`);
      toast.success('Invitation sent!');
      setSelectedParticipant(null);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send invitation');
    }
  };

  const handleDonationToggle = async (tripId, userId, confirmed) => {
    try {
      await api.put(`/trips/${tripId}/participants/${userId}/donation`, { confirmed });
      toast.success(confirmed ? 'Donation confirmed' : 'Donation mark removed');
      fetchAll();
    } catch {
      toast.error('Failed to update donation');
    }
  };

  const handleNoBagToggle = async (tripId, userId, noBag) => {
    try {
      await api.put(`/trips/${tripId}/participants/${userId}/no-bag`, { no_bag_needed: noBag });
      toast.success(noBag ? 'Marked as no bag needed' : 'Bag status reset to pending');
      fetchAll();
    } catch {
      toast.error('Failed to update bag status');
    }
  };


  const handleSaveNotes = async (userId, notes) => {
    try {
      await api.put(`/profiles/${userId}/notes`, { admin_notes: notes });
      toast.success('Notes saved');
      // Update selectedParticipant so the panel shows the new notes without full reload
      setSelectedParticipant(prev => prev
        ? { ...prev, profile: { ...(prev.profile || {}), admin_notes: notes } }
        : prev
      );
      fetchAll();
    } catch {
      toast.error('Failed to save notes');
    }
  };

  const handleProspectStatus = async (prospectId, status, emailType = null) => {
    try {
      const params = { status };
      if (emailType) params.email_type = emailType;
      await api.put(`/prospects/${prospectId}/status`, null, { params });
      toast.success(emailType ? `Prospect ${status} — email sent` : `Prospect ${status}`);
      fetchAll();
    } catch {
      toast.error('Failed to update prospect');
    }
  };

  const handleSendProspectTripInvite = async (userId, tripId) => {
    try {
      await api.post(`/trips/${tripId}/participants/${userId}/invite`);
      // Find the prospect by approved_user_id and update their status to "invited"
      const prospect = prospects.find(p => p.approved_user_id === userId);
      if (prospect) {
        await api.put(`/prospects/${prospect.prospect_id}/status`, null, { params: { status: 'invited' } }).catch(() => {});
      }
      toast.success('Trip invitation sent!');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send invitation');
    }
  };

  const handleDeleteProspect = async (prospectId, name) => {
    if (!window.confirm(`Delete prospect ${name || 'this person'}? This cannot be undone.`)) return;
    try {
      await api.delete(`/prospects/${prospectId}`);
      toast.success('Prospect deleted');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };


  const handleUnassign = async (tripId, userId, tripName) => {
    if (!window.confirm(`Unassign this participant from ${tripName || 'this trip'}?`)) return;

    // Optimistic: drop the assignment from local state immediately so the
    // Week row disappears without a full-list flash.
    setAllRoster(prev => prev.filter(p => !(p.user_id === userId && p.trip_id === tripId)));
    // Close the profile panel — its data would otherwise show stale rows
    // until the panel-modal's own refetch lands.
    setSelectedParticipant(null);

    try {
      await api.delete(`/trips/${tripId}/participants/${userId}`);
      toast.success('Participant unassigned from trip');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to unassign');
      // Rollback: refetch truth from server
      fetchAll();
    }
  };

  const handleGuardianAction = async (type, participant) => {
    if (type === 'unlink') {
      if (!window.confirm('Remove this guardian link?')) return;
      const link = guardianLinks.find(l => l.guardian_user_id === participant.user_id || l.dependent_user_id === participant.user_id);
      if (link) {
        try {
          await api.delete(`/guardian-links/${link.link_id}`);
          toast.success('Guardian link removed');
          fetchAll();
        } catch { toast.error('Failed to remove link'); }
      }
    } else {
      setGuardianDialog({ type, participant });
    }
  };

  const handleCreateGuardianLink = async (otherUserId) => {
    if (!guardianDialog) return;
    const { type, participant } = guardianDialog;
    try {
      const body = type === 'set-guardian'
        ? { guardian_user_id: otherUserId, dependent_user_id: participant.user_id, relationship: 'parent' }
        : { guardian_user_id: participant.user_id, dependent_user_id: otherUserId, relationship: 'parent' };
      await api.post('/guardian-links', body);
      toast.success('Guardian link created');
      setGuardianDialog(null);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create link');
    }
  };

  // Enrich participant data with guardian info
  const enrichWithGuardian = (p) => {
    const asGuardian = guardianLinks.find(l => l.guardian_user_id === p.user_id);
    const asDependent = guardianLinks.find(l => l.dependent_user_id === p.user_id);
    if (asGuardian) {
      const dep = allRoster.find(r => r.user_id === asGuardian.dependent_user_id);
      p.dependent_name = dep ? `${dep.profile?.first_name || ''} ${dep.profile?.last_name || ''}`.trim() || dep.user?.name : asGuardian.dependent_user_id;
    }
    if (asDependent) {
      const grd = allRoster.find(r => r.user_id === asDependent.guardian_user_id);
      p.guardian_name = grd ? `${grd.profile?.first_name || ''} ${grd.profile?.last_name || ''}`.trim() || grd.user?.name : asDependent.guardian_user_id;
    }
    return p;
  };



  const handleResendInvite = async (inviteId) => {
    setResendingId(inviteId);
    try {
      const res = await api.post(`/invites/${inviteId}/resend`);
      toast.success(res.data.message || 'Invitation resent');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to resend invitation');
    } finally {
      setResendingId(null);
    }
  };

  const handleNudgeExisting = async (inv) => {
    setResendingId(inv.id);
    try {
      const res = await api.post('/invites/nudge-existing', { user_id: inv.user_id, trip_id: inv.trip_id });
      toast.success(res.data.message || 'Reminder sent');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send reminder');
    } finally {
      setResendingId(null);
    }
  };

  const handleNudgeAll = async () => {
    if (!window.confirm(`Send reminders to all ${pendingInvites.length} pending invitations?`)) return;
    setNudgingAll(true);
    try {
      const res = await api.post('/invites/nudge-all');
      toast.success(res.data.message || 'All reminders sent');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send reminders');
    } finally {
      setNudgingAll(false);
    }
  };

  const handleDismissInvite = async (inv) => {
    const displayName = inv.name || inv.email;
    const tripLabel = inv.trip_name ? ` on ${inv.trip_name}` : '';
    if (!window.confirm(
      `Cancel this invitation for ${displayName}${tripLabel}? ` +
      `They will be removed from this trip and can be re-invited later.`
    )) return;

    // Optimistic: remove from pending list immediately so the row disappears
    // even before the server responds. Also clear the (Pending) badge on the
    // main participants row so admin sees the state resolve cleanly.
    setPendingInvites(prev => prev.filter(p => p.id !== inv.id));
    if (inv.type === 'existing' && inv.user_id && inv.trip_id) {
      const clearBadge = (a) =>
        a.user_id === inv.user_id && a.trip_id === inv.trip_id
          ? { ...a, participant_status: 'unassigned', dismissed_from_pending: true }
          : a;
      setAllRoster(prev => prev.map(p => ({
        ...p,
        ...(p.user_id === inv.user_id && p.trip_id === inv.trip_id
          ? { participant_status: 'unassigned', dismissed_from_pending: true } : {}),
        allAssignments: (p.allAssignments || []).map(clearBadge),
      })));
    }

    try {
      await api.post('/invites/dismiss', {
        type: inv.type,
        id: inv.id,
        user_id: inv.user_id,
        trip_id: inv.trip_id,
      });
      toast.success('Invitation cancelled');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to cancel');
      // Rollback on failure: reload to restore truth
      fetchAll();
    }
  };

  // Batch invite: get all users not assigned to the selected trip
  const getBatchEligible = () => {
    if (!batchTripId) return [];
    // Get all unique users from allRoster
    const userMap = {};
    allRoster.forEach(p => {
      if (p.user && !userMap[p.user_id]) {
        userMap[p.user_id] = {
          user_id: p.user_id,
          name: p.user?.name || `${p.profile?.first_name || ''} ${p.profile?.last_name || ''}`.trim() || p.user?.email,
          email: p.user?.email,
          work_roles: p.profile?.work_roles || [],
        };
      }
    });
    // Filter out users already assigned to this trip
    const assignedUserIds = new Set(
      allRoster.filter(p => p.trip_id === batchTripId && p.participant_status !== 'unassigned').map(p => p.user_id)
    );
    const eligible = Object.values(userMap).filter(u => !assignedUserIds.has(u.user_id));
    // Apply search filter
    if (batchSearch) {
      const term = batchSearch.toLowerCase();
      return eligible.filter(u => u.name?.toLowerCase().includes(term) || u.email?.toLowerCase().includes(term));
    }
    return eligible;
  };

  const handleBatchInvite = async () => {
    if (!batchTripId || batchSelected.size === 0) return;
    setBatchSending(true);
    let sent = 0;
    let failed = 0;
    for (const userId of batchSelected) {
      const user = allRoster.find(p => p.user_id === userId)?.user;
      if (!user) continue;
      try {
        await api.post('/participants/invite-new', { name: user.name, email: user.email, trip_id: batchTripId });
        sent++;
      } catch {
        failed++;
      }
    }
    toast.success(`${sent} invitation${sent !== 1 ? 's' : ''} sent${failed > 0 ? `, ${failed} failed` : ''}`);
    setBatchSending(false);
    setShowBatchInvite(false);
    setBatchSelected(new Set());
    fetchAll();
  };

  const handleAddParticipant = async () => {
    const { name, email, trip_id, role } = addForm;
    if (!name.trim() || !email.trim()) {
      toast.error('Name and email are required');
      return;
    }
    setAddSending(true);
    try {
      await api.post('/participants/invite-new', {
        name: name.trim(),
        email: email.trim(),
        trip_id: trip_id || undefined,
        role: role,
      });
      toast.success(trip_id ? `Invitation sent to ${email.trim()}` : `${name.trim()} invited to the system`);
      setShowAddDialog(false);
      setAddForm({ name: '', email: '', trip_id: '', role: 'participant' });
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add participant');
    } finally {
      setAddSending(false);
    }
  };

  // Group roster by user_id — one row per person showing all their trips
  const groupByUser = (data) => {
    const grouped = {};
    data.forEach(p => {
      const uid = p.user_id;
      if (!grouped[uid]) {
        grouped[uid] = { ...p, trips: [p.trip], allAssignments: [p] };
      } else {
        if (p.trip) grouped[uid].trips.push(p.trip);
        grouped[uid].allAssignments.push(p);
        // Merge task completion — a task is "done" only if done on all trips (or it's profile-level)
        // For display purposes, show merged status
      }
    });
    return Object.values(grouped);
  };

  // Filter logic — operate on grouped data
  const applyFilters = (data) => {
    const grouped = groupByUser(data);
    return grouped.filter(p => {
      const name = p.profile?.first_name
        ? `${p.profile.first_name} ${p.profile.last_name || ''}`.toLowerCase()
        : (p.user?.name || '').toLowerCase();
      const email = (p.user?.email || '').toLowerCase();
      const term = search.toLowerCase();
      if (term && !name.includes(term) && !email.includes(term)) return false;
      if (filterTrip !== 'all' && !(p.allAssignments || []).some(a => a.trip_id === filterTrip)) return false;
      if (filterStatus === 'unassigned') {
        if (p.participant_status !== 'unassigned') return false;
      } else if (filterStatus !== 'all' && !(p.allAssignments || []).some(a => a.participant_status === filterStatus)) return false;
      if (filterRoles.length > 0) {
        const color = getRoleColor(p.profile?.work_roles || []);
        if (!filterRoles.includes(color || 'none')) return false;
      }
      if (filterStates.length > 0 && !filterStates.includes((p.profile?.state || '').trim().toUpperCase())) return false;
      return true;
    });
  };

  // Sort logic
  const applySorting = (data) => {
    const getName = (p) => {
      const fn = p.profile?.first_name || '';
      const ln = p.profile?.last_name || '';
      return `${fn} ${ln}`.trim().toLowerCase() || (p.user?.name || '').toLowerCase();
    };

    const getTaskCount = (p) => {
      const t = p.tasks || {};
      const keys = ['profile_complete', 'passport_complete', 'waiver_signed', 'flight_submitted', 'hotel_preferences', 'donation_confirmed'];
      return keys.filter(k => !!t[k]).length;
    };

    const getTaskTotal = () => 6;

    const hasMissing = (p, key) => !(p.tasks || {})[key];

    return [...data].sort((a, b) => {
      let cmp = 0;

      switch (sortPrimary) {
        case 'none':
          break;
        case 'most-complete':
          cmp = getTaskCount(b) - getTaskCount(a);
          break;
        case 'least-complete':
          cmp = getTaskCount(a) - getTaskCount(b);
          break;
        case 'missing-profile':
          cmp = (hasMissing(a, 'profile_complete') ? 0 : 1) - (hasMissing(b, 'profile_complete') ? 0 : 1);
          break;
        case 'missing-passport':
          cmp = (hasMissing(a, 'passport_complete') ? 0 : 1) - (hasMissing(b, 'passport_complete') ? 0 : 1);
          break;
        case 'missing-waiver':
          cmp = (hasMissing(a, 'waiver_signed') ? 0 : 1) - (hasMissing(b, 'waiver_signed') ? 0 : 1);
          break;
        case 'missing-flight':
          cmp = (hasMissing(a, 'flight_submitted') ? 0 : 1) - (hasMissing(b, 'flight_submitted') ? 0 : 1);
          break;
        case 'missing-hotel':
          cmp = (hasMissing(a, 'hotel_preferences') ? 0 : 1) - (hasMissing(b, 'hotel_preferences') ? 0 : 1);
          break;
        case 'missing-donation':
          cmp = (hasMissing(a, 'donation_confirmed') ? 0 : 1) - (hasMissing(b, 'donation_confirmed') ? 0 : 1);
          break;
        default:
          break;
      }

      // Secondary sort — always name
      if (cmp === 0) {
        cmp = sortSecondary === 'name-za'
          ? getName(b).localeCompare(getName(a))
          : getName(a).localeCompare(getName(b));
      }

      return cmp;
    });
  };

  const displayData = applySorting(activeTab === 'current' ? applyFilters(roster) : applyFilters(allRoster));

  // States present in the currently active dataset, for the State filter dropdown.
  // Normalized to uppercase and de-duplicated so "WI" and "Wi" don't show as separate entries.
  const availableStates = [...new Set(
    groupByUser(activeTab === 'current' ? roster : allRoster)
      .map(p => (p.profile?.state || '').trim().toUpperCase())
      .filter(Boolean)
  )].sort((a, b) => a.localeCompare(b));

  const toggleFilterState = (state) => {
    setFilterStates(prev => prev.includes(state) ? prev.filter(s => s !== state) : [...prev, state]);
  };

  const toggleFilterRole = (role) => {
    setFilterRoles(prev => prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role]);
  };

  // Selection helpers for group email — look up email/name across all participants regardless of active tab
  const allFilteredSelected = displayData.length > 0 && displayData.every(p => selectedEmails.has(p.user_id));
  const toggleSelectAllFiltered = () => {
    setSelectedEmails(prev => {
      const next = new Set(prev);
      displayData.forEach(p => allFilteredSelected ? next.delete(p.user_id) : next.add(p.user_id));
      return next;
    });
  };
  const selectedGroupRecipients = [...selectedEmails].map(uid => {
    const p = allRoster.find(r => r.user_id === uid);
    return {
      user_id: uid,
      email: p?.user?.email,
      name: p?.profile?.first_name ? `${p.profile.first_name} ${p.profile.last_name || ''}`.trim() : (p?.user?.name || p?.user?.email),
    };
  }).filter(r => r.email);

  // Admins / elevated-access users — same list as the Permissions page — offered as CC suggestions
  const ccSuggestions = adminUsers.filter(u => (u.role === 'admin' || u.elevated_access === true) && u.email);
  const currentCcList = groupEmailForm.cc.split(/[,;]/).map(s => s.trim()).filter(Boolean);

  const pendingInterests = tripInterests.filter(i => i.status === 'pending');
  const counts = {
    current: groupByUser(roster).length,
    all: groupByUser(allRoster).length,
    prospects: prospects.length,
    requests: pendingInterests.length,
  };

  // ── Render a participant row ───────────────────────────────
  const renderRow = (p, idx) => {
    const name = p.profile?.first_name
      ? `${p.profile.first_name} ${p.profile.last_name || ''}`.trim()
      : p.user?.name || 'Unknown';
    const roles = p.profile?.work_roles || [];
    const roleColor = getRoleColor(roles);
    const rowClass = ROW_COLORS[roleColor || 'none'] || ROW_COLORS.none;
    // Gold star: only from responsibilities (trip_role is for access control only)
    const responsibilities = p.responsibilities || [];
    const isLeader = responsibilities.some(r => r.toLowerCase().includes('leader'));
    const tasks = p.tasks || {};
    // Medical specialty display — show first work_role, fallback to org role
    const specialtyDisplay = roles.length > 0 ? roles[0] : null;
    // Responsibilities as text (max 2 shown inline)
    const respDisplay = responsibilities.slice(0, 2).join(' · ');
    // Minor check — use earliest trip start_date as reference
    const tripStartDates = (p.trips || [p.trip]).filter(Boolean).map(t => t?.start_date).filter(Boolean);
    const minorRef = tripStartDates.length > 0 ? tripStartDates.sort()[0] : null;
    const minor = isMinorAsOf(p.profile?.date_of_birth, minorRef);

    return (
      <div
        key={p.user_id}
        className={`grid grid-cols-[32px_28px_minmax(220px,2fr)_minmax(185px,1.3fr)_minmax(220px,1.3fr)_100px] min-w-[800px] items-center border-b border-slate-100 last:border-0 cursor-pointer transition-colors ${rowClass} ${selectedParticipant?.user_id === p.user_id ? 'ring-1 ring-inset ring-primary' : ''}`}
        onClick={() => setSelectedParticipant(selectedParticipant?.user_id === p.user_id ? null : p)}
        data-testid={`participant-row-${p.user_id}`}
      >
        {/* Group email selection checkbox */}
        <div className="px-2 py-3 flex items-center justify-center" onClick={e => e.stopPropagation()}>
          <Checkbox
            checked={selectedEmails.has(p.user_id)}
            onCheckedChange={() => toggleSelectParticipant(p.user_id)}
            data-testid={`select-participant-${p.user_id}`}
          />
        </div>

        {/* Row number */}
        <div className="px-2 py-3 text-xs text-slate-400 text-right">{idx + 1}</div>

        {/* Name + medical specialty + responsibilities */}
        <div className="px-3 py-3 min-w-0">
          <div className="flex items-center gap-1.5">
            {isLeader && <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400 flex-shrink-0" />}
            <span className="font-medium text-slate-800 whitespace-nowrap text-sm">{name}</span>
            {minor && <Badge className="bg-purple-100 text-purple-700 border-0 text-[10px] px-1.5 py-0 flex-shrink-0" data-testid="minor-badge">Minor</Badge>}
          </div>
          {specialtyDisplay && (
            <span className="text-xs text-slate-600 whitespace-nowrap block font-medium">
              {specialtyDisplay}
              {p.profile?.organization ? ` · ${p.profile.organization}` : ''}
            </span>
          )}
          {respDisplay && (
            <span className="text-xs text-primary/70 whitespace-nowrap block">{respDisplay}</span>
          )}
          {!specialtyDisplay && !respDisplay && (
            <span className="text-xs text-slate-400">No role on file</span>
          )}
        </div>

        {/* Trip(s) — show all */}
        <div className="px-3 py-3 min-w-0">
          {(p.allAssignments || [p]).map((assign, ti) => {
            const t = assign.trip;
            const isPending = assign.participant_status === 'invited' && !assign.dismissed_from_pending;
            return (
              <span key={t?.trip_id || ti} className="text-xs text-slate-600 block leading-tight">
                {t?.name || '—'}
                {isPending && <span className="ml-1 text-[10px] text-amber-600 font-medium">(Pending)</span>}
              </span>
            );
          })}
        </div>

        {/* Task icons */}
        <div className="px-3 py-3 flex items-center gap-1.5 flex-wrap">
          {TASK_DEFS.map(({ key, icon, label }) => {
            // Skip license icon if not required for this participant
            if (key === 'license_uploaded' && !tasks?.license_required) return null;
            // Check for expiration warnings
            let warning = false;
            if (key === 'passport_complete' && tasks?.passport_complete) {
              const exp = p.profile?.passport_expiration;
              if (exp) {
                const sixMonths = new Date();
                sixMonths.setMonth(sixMonths.getMonth() + 6);
                if (new Date(exp) < sixMonths) warning = true;
              }
            }
            // Waiver partial — orange when main waiver signed but addendum incomplete
            let warningText = '';
            if (key === 'passport_complete' && warning) warningText = 'Expiring Soon';
            if (key === 'waiver_signed' && tasks?.waiver_partial) { warning = true; warningText = 'Partial'; }
            return <TaskIcon key={key} done={!!tasks[key]} icon={icon} label={label} warning={warning} warningText={warningText} />;
          })}
        </div>

        {/* Contact shortcuts */}
        <div className="px-3 py-3 flex items-center gap-2">
          {p.profile?.phone && (
            <a
              href={`tel:${(p.profile.phone_country_code || '+1').replace(/\s/g, '')}${p.profile.phone.replace(/\D/g, '')}`}
              onClick={e => e.stopPropagation()}
              className="text-slate-300 hover:text-green-600 transition-colors"
              title={`${p.profile.phone_country_code || '+1'} ${p.profile.phone}`}
            >
              <Phone className="w-4 h-4" />
            </a>
          )}
          {p.user?.email && (
            <button
              onClick={e => { e.stopPropagation(); setComposeEmail({ to_email: p.user.email, name: `${p.profile?.first_name || ''} ${p.profile?.last_name || ''}`.trim() || p.user.name }); setEmailForm({ subject: '', message: '' }); }}
              className="text-slate-300 hover:text-primary transition-colors"
              title={`Email ${p.user.email}`}
              data-testid={`email-participant-${p.user_id}`}
            >
              <Mail className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    );
  };

  const partActions = activeTab !== 'prospects' ? (
    <>
      <Button size="sm" className="rounded-full bg-primary hover:bg-primary/90 h-8 text-xs px-2 sm:px-3"
        onClick={handleEmailGroupClick} data-testid="email-group-btn">
        <Mail className="w-3.5 h-3.5 sm:mr-1.5" /><span className="hidden sm:inline">Email Group{selectedEmails.size > 0 ? ` (${selectedEmails.size})` : ''}</span>
      </Button>
      <Button size="sm" className="rounded-full bg-rojo hover:bg-rojo-dark h-8 text-xs px-2 sm:px-3"
        onClick={() => { setBatchTripId(''); setBatchSelected(new Set()); setBatchSearch(''); setShowBatchInvite(true); }}
        data-testid="batch-invite-btn">
        <Users className="w-3.5 h-3.5 sm:mr-1.5" /><span className="hidden sm:inline">Invite to Trip</span>
      </Button>
      <Button size="sm" variant="outline" className="rounded-full h-8 text-xs px-2 sm:px-3"
        onClick={() => setShowAddDialog(true)} data-testid="add-participant-btn">
        <Send className="w-3.5 h-3.5 sm:mr-1.5" /><span className="hidden sm:inline">Add New</span>
      </Button>
      <a href={`${process.env.REACT_APP_BACKEND_URL}/api/participants/print${filterTrip !== 'all' ? `?trip_id=${filterTrip}` : `?year=${currentYear}`}`}
        target="_blank" rel="noopener noreferrer" data-testid="print-participants-btn">
        <Button variant="outline" size="sm" className="rounded-full h-8 text-xs px-2 sm:px-3">
          <Printer className="w-3.5 h-3.5 sm:mr-1.5" /><span className="hidden sm:inline">Print Roster</span>
        </Button>
      </a>
      <a href={`${process.env.REACT_APP_BACKEND_URL}/api/participants/export-excel`} data-testid="export-excel-btn">
        <Button variant="outline" size="sm" className="rounded-full h-8 text-xs px-2 sm:px-3">
          <Download className="w-3.5 h-3.5 sm:mr-1.5" /><span className="hidden sm:inline">Export Excel</span>
        </Button>
      </a>
    </>
  ) : null;

  return (
    <PageLayout title="Participants" headerActions={partActions}>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <div className="mb-6 overflow-x-auto scrollbar-thin">
              <TabsList className="w-max min-w-full">
                <TabsTrigger value="current" data-testid="tab-current" className="text-xs sm:text-sm whitespace-nowrap">
                  <span className="sm:hidden">{currentYear}</span>
                  <span className="hidden sm:inline">Current Year {currentYear}</span>
                  <Badge className="ml-2 bg-primary/10 text-primary border-0 text-xs">{counts.current}</Badge>
                </TabsTrigger>
                <TabsTrigger value="all" data-testid="tab-all" className="text-xs sm:text-sm whitespace-nowrap">
                  <span className="sm:hidden">All</span>
                  <span className="hidden sm:inline">All Participants</span>
                  <Badge className="ml-2 bg-slate-100 text-slate-600 border-0 text-xs">{counts.all}</Badge>
                </TabsTrigger>
                <TabsTrigger value="prospects" data-testid="tab-prospects" className="text-xs sm:text-sm whitespace-nowrap">
                  Prospects
                  <Badge className="ml-2 bg-yellow-100 text-yellow-700 border-0 text-xs">{counts.prospects}</Badge>
                </TabsTrigger>
                <TabsTrigger value="requests" data-testid="tab-requests" className="text-xs sm:text-sm whitespace-nowrap">
                  <span className="sm:hidden">Requests</span>
                  <span className="hidden sm:inline">Trip Requests</span>
                  {counts.requests > 0 && <Badge className="ml-2 bg-rojo/10 text-rojo border-0 text-xs">{counts.requests}</Badge>}
                </TabsTrigger>
              </TabsList>
            </div>

            {/* ── Current Year + All share the same table UI ── */}
            {(activeTab === 'current' || activeTab === 'all') && (
              <div>
                {/* Filter bar */}
                <div className="flex flex-wrap items-center gap-2 mb-4">
                  <div className="relative w-full sm:w-48">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 h-9 w-full" data-testid="search-input" />
                  </div>
                  <Select value={filterTrip} onValueChange={setFilterTrip}>
                    <SelectTrigger className="h-9 w-[calc(50%-4px)] sm:w-56" data-testid="filter-trip">
                      <SelectValue placeholder="All Trips" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Trips</SelectItem>
                      {trips.map(t => <SelectItem key={t.trip_id} value={t.trip_id}>{t.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={sortPrimary} onValueChange={setSortPrimary}>
                    <SelectTrigger className="h-9 w-[calc(50%-4px)] sm:w-48" data-testid="sort-primary">
                      <SelectValue placeholder="Sort by..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Task Sort</SelectItem>
                      <SelectItem value="most-complete">Most Complete</SelectItem>
                      <SelectItem value="least-complete">Least Complete</SelectItem>
                      <SelectItem value="missing-profile">Missing Profile</SelectItem>
                      <SelectItem value="missing-passport">Missing Passport</SelectItem>
                      <SelectItem value="missing-waiver">Missing Waiver</SelectItem>
                      <SelectItem value="missing-flight">Missing Flight</SelectItem>
                      <SelectItem value="missing-hotel">Missing Hotel</SelectItem>
                      <SelectItem value="missing-donation">Missing Donation</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={sortSecondary} onValueChange={setSortSecondary}>
                    <SelectTrigger className="h-9 w-[calc(50%-4px)] sm:w-36" data-testid="sort-secondary">
                      <SelectValue placeholder="Then by..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="name-az">Name A–Z</SelectItem>
                      <SelectItem value="name-za">Name Z–A</SelectItem>
                    </SelectContent>
                  </Select>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" size="sm"
                        className="h-9 w-[calc(50%-4px)] sm:w-40 justify-between font-normal text-slate-700"
                        data-testid="filter-role">
                        <span className="truncate">
                          {filterRoles.length === 0 ? 'All Roles' : filterRoles.length === 1 ? ROLE_FILTER_OPTIONS.find(r => r.value === filterRoles[0])?.label : `${filterRoles.length} Roles`}
                        </span>
                        <ChevronDown className="w-3.5 h-3.5 text-slate-400 flex-shrink-0 ml-1" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-56 p-2" align="start">
                      <div className="flex items-center justify-between mb-1.5 px-1">
                        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Roles</span>
                        {filterRoles.length > 0 && (
                          <button onClick={() => setFilterRoles([])} className="text-xs text-primary hover:underline" data-testid="clear-role-filter">
                            Clear
                          </button>
                        )}
                      </div>
                      <div className="space-y-0.5">
                        {ROLE_FILTER_OPTIONS.map(r => (
                          <label key={r.value} className="flex items-center gap-2 px-1.5 py-1.5 rounded-lg hover:bg-slate-50 cursor-pointer text-sm">
                            <Checkbox
                              checked={filterRoles.includes(r.value)}
                              onCheckedChange={() => toggleFilterRole(r.value)}
                              data-testid={`role-option-${r.value}`}
                            />
                            <span className="text-slate-700">{r.label}</span>
                          </label>
                        ))}
                      </div>
                    </PopoverContent>
                  </Popover>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" size="sm"
                        className="h-9 w-[calc(50%-4px)] sm:w-40 justify-between font-normal text-slate-700"
                        data-testid="filter-state">
                        <span className="truncate">
                          {filterStates.length === 0 ? 'All States' : filterStates.length === 1 ? filterStates[0] : `${filterStates.length} States`}
                        </span>
                        <ChevronDown className="w-3.5 h-3.5 text-slate-400 flex-shrink-0 ml-1" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-56 p-2" align="start">
                      <div className="flex items-center justify-between mb-1.5 px-1">
                        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">States</span>
                        {filterStates.length > 0 && (
                          <button onClick={() => setFilterStates([])} className="text-xs text-primary hover:underline" data-testid="clear-state-filter">
                            Clear
                          </button>
                        )}
                      </div>
                      <div className="max-h-56 overflow-y-auto space-y-0.5">
                        {availableStates.length === 0 ? (
                          <p className="text-xs text-slate-400 px-1.5 py-2">No state data yet</p>
                        ) : availableStates.map(s => (
                          <label key={s} className="flex items-center gap-2 px-1.5 py-1.5 rounded-lg hover:bg-slate-50 cursor-pointer text-sm">
                            <Checkbox
                              checked={filterStates.includes(s)}
                              onCheckedChange={() => toggleFilterState(s)}
                              data-testid={`state-option-${s}`}
                            />
                            <span className="text-slate-700">{s}</span>
                          </label>
                        ))}
                      </div>
                    </PopoverContent>
                  </Popover>
                  {(filterTrip !== 'all' || filterRoles.length > 0 || filterStates.length > 0 || search || sortPrimary !== 'none') && (
                    <Button variant="ghost" size="sm" className="h-9 text-slate-500"
                      onClick={() => { setFilterTrip('all'); setFilterRoles([]); setFilterStates([]); setSearch(''); setSortPrimary('none'); setSortSecondary('name-az'); }}>
                      <X className="w-3.5 h-3.5 mr-1" />Clear
                    </Button>
                  )}
                  {selectedEmails.size > 0 && (
                    <>
                      <Button variant="ghost" size="sm" className="h-9 text-slate-400" onClick={() => setSelectedEmails(new Set())} data-testid="clear-selection-btn">
                        <X className="w-3.5 h-3.5 mr-1" />{selectedEmails.size} selected — Clear
                      </Button>
                    </>
                  )}
                  <span className="ml-auto text-sm text-slate-400 hidden sm:block">{displayData.length} participant{displayData.length !== 1 ? 's' : ''}</span>
                </div>

                {/* Color legend - hide on very small screens, show compact on mobile */}
                <div className="hidden sm:flex flex-wrap gap-3 mb-4 text-xs">
                  {[
                    { color: 'bg-orange-100', label: 'Surgeons / Physicians' },
                    { color: 'bg-yellow-100', label: 'Anesthesia / CRNA' },
                    { color: 'bg-blue-100',   label: 'OR Nurses / Techs' },
                    { color: 'bg-green-100',  label: 'PACU / Recovery' },
                  ].map(l => (
                    <span key={l.label} className="flex items-center gap-1.5">
                      <span className={`w-3 h-3 rounded-sm ${l.color}`} />
                      <span className="text-slate-500">{l.label}</span>
                    </span>
                  ))}
                  <span className="flex items-center gap-1.5">
                    <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
                    <span className="text-slate-500">Trip Leader</span>
                  </span>
                </div>

                {/* Table */}
                <div className="bg-white border border-slate-200 rounded-xl overflow-hidden overflow-x-auto">
                  {/* Header */}
                  <div className="grid grid-cols-[32px_28px_minmax(220px,2fr)_minmax(185px,1.3fr)_minmax(220px,1.3fr)_100px] min-w-[800px] bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                    <div className="px-2 py-3 flex items-center justify-center">
                      <Checkbox checked={allFilteredSelected} onCheckedChange={toggleSelectAllFiltered} data-testid="select-all-checkbox" />
                    </div>
                    <div className="px-2 py-3 text-right">#</div>
                    <div className="px-3 py-3">Name / Role</div>
                    <div className="px-3 py-3">Trip</div>
                    <div className="px-3 py-3">Tasks <span className="text-slate-400 font-normal normal-case tracking-normal">(hover)</span></div>
                    <div className="px-3 py-3"></div>
                  </div>

                  {loading ? (
                    <div className="text-center py-16 text-slate-400">Loading...</div>
                  ) : displayData.length === 0 ? (
                    <div className="text-center py-16">
                      <Users className="w-10 h-10 text-slate-200 mx-auto mb-3" />
                      <p className="text-slate-500 text-sm">
                        {activeTab === 'current' ? `No participants registered for ${currentYear} yet.` : 'No participants found.'}
                      </p>
                      {activeTab === 'current' && (
                        <p className="text-slate-400 text-xs mt-1">Participants who request a trip will appear here.</p>
                      )}
                    </div>
                  ) : (
                    displayData.map((p, i) => renderRow(p, i))
                  )}
                </div>
              </div>
            )}

            {/* ── Prospects tab ── */}
            <TabsContent value="prospects">
              <div className="space-y-3 max-w-3xl">
                {loading ? (
                  <div className="text-center py-16 text-slate-400">Loading...</div>
                ) : prospects.length === 0 ? (
                  <div className="text-center py-16">
                    <Users className="w-10 h-10 text-slate-200 mx-auto mb-3" />
                    <p className="text-slate-500 text-sm">No prospects yet.</p>
                    <p className="text-slate-400 text-xs mt-1">Prospects come from the public sign-up form at /signup</p>
                  </div>
                ) : (
                  prospects.map(p => (
                    <ProspectCard key={p.prospect_id} prospect={p} onStatusUpdate={handleProspectStatus} onSendTripInvite={handleSendProspectTripInvite} onDelete={handleDeleteProspect} trips={trips} />
                  ))
                )}
              </div>
            </TabsContent>

            {/* ── Trip Requests Tab ── */}
            <TabsContent value="requests">
              <div className="space-y-4">
                {/* Summary */}
                <p className="text-sm text-slate-500">
                  {tripInterests.filter(i => i.status === 'pending').length} pending request{tripInterests.filter(i => i.status === 'pending').length !== 1 ? 's' : ''}
                </p>

                {/* Request cards */}
                {tripInterests.length === 0 ? (
                  <Card className="text-center py-12">
                    <CardContent>
                      <Heart className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                      <p className="text-slate-500">No trip interest requests</p>
                      <p className="text-sm text-slate-400 mt-1">Participants can submit interest from the Trip Interest page</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-3">
                    {tripInterests.map(interest => {
                      const profile = interest.profile || {};
                      const user = interest.user || {};
                      const name = `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || user.name || user.email;
                      const roles = (profile.work_roles || []).join(', ');
                      const prefTrips = trips.filter(t => (interest.preferred_trip_ids || []).includes(t.trip_id));
                      // Find existing trip assignments for this user
                      const userAssignments = allRoster.filter(r => r.user_id === interest.user_id && r.trip);

                      return (
                        <Card key={`${interest.user_id}_${interest.year}`} className={`${interest.status === 'approved' ? 'border-green-200 bg-green-50/30' : interest.status === 'waitlisted' ? 'border-amber-200 bg-amber-50/30' : interest.status === 'declined' ? 'border-red-200 bg-red-50/30 opacity-60' : ''}`}>
                          <CardContent className="py-4">
                            <div className="flex flex-col sm:flex-row sm:items-start gap-3">
                              {/* Person info */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <p className="font-medium text-slate-800">{name}</p>
                                  {interest.status !== 'pending' && (
                                    <Badge className={`text-xs ${interest.status === 'approved' ? 'bg-green-100 text-green-700' : interest.status === 'waitlisted' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-600'}`}>
                                      {interest.status}
                                    </Badge>
                                  )}
                                </div>
                                {roles && <p className="text-sm text-primary mt-0.5">{roles}</p>}
                                <p className="text-xs text-slate-400 mt-0.5">{user.email}</p>

                                {/* Requesting — prominent */}
                                <div className="mt-3 p-2.5 bg-blue-50 border border-blue-200 rounded-lg">
                                  <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide mb-1">Requesting</p>
                                  <div className="flex flex-wrap gap-1.5">
                                    {prefTrips.map(t => (
                                      <Badge key={t.trip_id} className="bg-blue-100 text-blue-800 border-0 text-xs font-semibold">{t.name}</Badge>
                                    ))}
                                    {prefTrips.length === 0 && <span className="text-xs text-blue-500 italic">No specific trip selected</span>}
                                  </div>
                                </div>

                                {/* Already assigned to — secondary */}
                                {userAssignments.length > 0 && (
                                  <div className="mt-2">
                                    <p className="text-xs text-slate-400 mb-1">Already assigned to:</p>
                                    <div className="flex flex-wrap gap-1.5">
                                      {userAssignments.map(a => (
                                        <Badge key={a.trip_id} variant="outline" className="text-xs text-slate-500">{a.trip?.name}</Badge>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {/* Notes */}
                                {interest.notes && (
                                  <p className="text-sm text-slate-600 mt-2 italic bg-slate-50 rounded-lg px-3 py-2">"{interest.notes}"</p>
                                )}

                                <p className="text-xs text-slate-400 mt-2">Submitted {new Date(interest.submitted_at).toLocaleDateString()}</p>
                              </div>

                              {/* Actions */}
                              {interest.status === 'pending' && (
                                <div className="flex flex-col gap-2 sm:w-44">
                                  {/* Direct approve buttons per requested trip */}
                                  {prefTrips.map(t => (
                                    <Button key={t.trip_id} size="sm" className="h-8 text-xs bg-green-600 hover:bg-green-700 rounded-full"
                                      onClick={async () => {
                                        try {
                                          await api.put(`/trip-interests/${interest.user_id}/${interest.year}/status`, { status: 'approved', assigned_trip_id: t.trip_id });
                                          toast.success(`${name} approved for ${t.name}`);
                                          fetchAll();
                                        } catch { toast.error('Failed'); }
                                      }}
                                      data-testid={`approve-${interest.user_id}-${t.trip_id}`}
                                    >
                                      Approve → {t.name?.replace(/October \d{4} - /, '')}
                                    </Button>
                                  ))}
                                  <Button size="sm" variant="outline" className="h-8 text-xs text-amber-600 border-amber-200 rounded-full"
                                    onClick={async () => {
                                      try {
                                        await api.put(`/trip-interests/${interest.user_id}/${interest.year}/status`, { status: 'waitlisted' });
                                        toast.success(`${name} waitlisted`);
                                        fetchAll();
                                      } catch { toast.error('Failed'); }
                                    }}>Waitlist</Button>
                                  <Button size="sm" variant="ghost" className="h-8 text-xs text-red-500 rounded-full"
                                    onClick={async () => {
                                      try {
                                        await api.put(`/trip-interests/${interest.user_id}/${interest.year}/status`, { status: 'declined' });
                                        toast.success(`${name} declined`);
                                        fetchAll();
                                      } catch { toast.error('Failed'); }
                                    }}>Decline</Button>
                                </div>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>

      {/* ── Pending Invitations Panel ── */}
      {pendingInvites.length > 0 && (
        <Card className="mt-6" data-testid="pending-invitations-panel">
          <button
            type="button"
            onClick={() => setPendingInvitesOpen(o => !o)}
            className="w-full text-left"
            data-testid="toggle-pending-invitations"
          >
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base font-heading">
                <Clock className="w-4 h-4 text-amber-500" />
                Pending Invitations
                <Badge className="ml-1 bg-amber-100 text-amber-700 border-0 text-xs">{pendingInvites.length}</Badge>
                {pendingInvitesOpen
                  ? <ChevronDown className="w-4 h-4 ml-auto text-slate-400" />
                  : <ChevronRight className="w-4 h-4 ml-auto text-slate-400" />}
              </CardTitle>
              <p className="text-xs text-slate-400">Invitations sent but not yet accepted</p>
            </CardHeader>
          </button>
          {pendingInvitesOpen && (
            <CardContent className="pt-0">
              {/* Sort, Filter, and Nudge All controls */}
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-0.5">
                  <button
                    onClick={() => setPendingSort('name')}
                    className={`px-2.5 py-1 text-xs rounded-md transition-colors ${pendingSort === 'name' ? 'bg-white shadow-sm text-slate-800 font-medium' : 'text-slate-500'}`}
                    data-testid="pending-sort-name"
                  >Name</button>
                  <button
                    onClick={() => setPendingSort('date')}
                    className={`px-2.5 py-1 text-xs rounded-md transition-colors ${pendingSort === 'date' ? 'bg-white shadow-sm text-slate-800 font-medium' : 'text-slate-500'}`}
                    data-testid="pending-sort-date"
                  >Date</button>
                </div>
                <Select value={pendingTripFilter} onValueChange={setPendingTripFilter}>
                  <SelectTrigger className="h-7 w-44 text-xs" data-testid="pending-trip-filter">
                    <SelectValue placeholder="All trips" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All trips</SelectItem>
                    {[...new Set(pendingInvites.map(i => i.trip_name).filter(Boolean))].sort().map(tn => (
                      <SelectItem key={tn} value={tn}>{tn}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="ml-auto">
                  <Button
                    size="sm"
                    className="rounded-full bg-amber-500 hover:bg-amber-600 text-white h-7 text-xs"
                    disabled={nudgingAll}
                    onClick={handleNudgeAll}
                    data-testid="nudge-all-btn"
                  >
                    <Send className={`w-3.5 h-3.5 mr-1.5 ${nudgingAll ? 'animate-pulse' : ''}`} />
                    {nudgingAll ? 'Sending...' : `Nudge All`}
                  </Button>
                </div>
              </div>
              <div className="divide-y divide-slate-100">
                {pendingInvites
                  .filter(inv => pendingTripFilter === 'all' || inv.trip_name === pendingTripFilter)
                  .sort((a, b) => {
                    if (pendingSort === 'name') {
                      return (a.name || a.email || '').localeCompare(b.name || b.email || '');
                    }
                    return (b.sent_at || '').localeCompare(a.sent_at || ''); // newest first
                  })
                  .map(inv => {
                  const isNew = inv.type === 'new';
                  const sentDate = inv.sent_at ? new Date(inv.sent_at).toLocaleDateString() : '—';
                  const daysSince = inv.sent_at ? Math.floor((Date.now() - new Date(inv.sent_at).getTime()) / 86400000) : 0;
                  return (
                    <div key={inv.id} className="flex flex-col gap-2 py-3" data-testid={`pending-invite-${inv.id}`}>
                      <div className="flex items-center gap-3 min-w-0">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${isNew ? 'bg-blue-50' : 'bg-green-50'}`}>
                          {isNew
                            ? <Mail className="w-4 h-4 text-blue-500" />
                            : <User className="w-4 h-4 text-green-600" />}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-sm font-medium text-slate-800 truncate">
                              {inv.name || inv.email}
                            </p>
                            <Badge className={`text-[10px] px-1.5 py-0 border-0 ${isNew ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
                              {isNew ? 'New' : 'Returning'}
                            </Badge>
                          </div>
                          <p className="text-xs text-slate-500 truncate">{inv.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap pl-11">
                        {inv.trip_name && (
                          <Badge variant="outline" className="text-xs">{inv.trip_name}</Badge>
                        )}
                        <span className="text-xs text-slate-400">
                          Sent {sentDate}{daysSince > 0 ? ` (${daysSince}d)` : ''}
                        </span>
                        {inv.expired && (
                          <span className="flex items-center gap-1 text-xs text-red-500">
                            <AlertTriangle className="w-3 h-3" />Expired
                          </span>
                        )}
                        <div className="flex items-center gap-1 ml-auto">
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs rounded-full"
                            disabled={resendingId === inv.id}
                            onClick={() => isNew ? handleResendInvite(inv.id) : handleNudgeExisting(inv)}
                            data-testid={`nudge-invite-${inv.id}`}
                          >
                            <Send className={`w-3 h-3 mr-1 ${resendingId === inv.id ? 'animate-pulse' : ''}`} />
                            {resendingId === inv.id ? 'Sending...' : 'Nudge'}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 w-7 p-0 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-full"
                            onClick={() => handleDismissInvite(inv)}
                            title="Dismiss this invitation"
                            data-testid={`dismiss-invite-${inv.id}`}
                          >
                            <X className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          )}
        </Card>
      )}

      {/* Detail panel */}
      {selectedParticipant && (
        <>
          <div className="fixed inset-0 z-30 bg-black/10" onClick={() => setSelectedParticipant(null)} />
          <DetailPanel
            participant={enrichWithGuardian({...selectedParticipant})}
            trips={trips}
            onClose={() => setSelectedParticipant(null)}
            onInvite={handleInvite}
            onDonationToggle={handleDonationToggle}
            onNoBagToggle={handleNoBagToggle}
            onAssignResponsibilities={handleOpenRespModal}
            onSaveNotes={handleSaveNotes}
            onViewProfile={(p) => setViewingProfile(p)}
            onUnassign={handleUnassign}
            onGuardianAction={handleGuardianAction}
            currentUser={authUser}
            onSaved={() => { fetchAll(); }}
          />
        </>
      )}

      {/* ── Add New Person Dialog ── */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Person</DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-4">
            <p className="text-sm text-slate-500">
              Add someone to the system. They'll receive an email invitation to set up their account.
            </p>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Full Name *</label>
              <Input
                value={addForm.name}
                onChange={e => setAddForm(f => ({ ...f, name: e.target.value }))}
                placeholder="Dr. Jane Smith"
                data-testid="add-participant-name"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Email Address *</label>
              <Input
                type="email"
                value={addForm.email}
                onChange={e => setAddForm(f => ({ ...f, email: e.target.value }))}
                placeholder="jane@hospital.com"
                data-testid="add-participant-email"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Role</label>
              <Select value={addForm.role} onValueChange={v => setAddForm(f => ({ ...f, role: v }))}>
                <SelectTrigger data-testid="add-participant-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="participant">Participant</SelectItem>
                  <SelectItem value="staff">Staff (non-trip)</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Assign to Trip <span className="text-xs text-slate-400 font-normal">(optional)</span></label>
              <Select value={addForm.trip_id || 'none'} onValueChange={v => setAddForm(f => ({ ...f, trip_id: v === 'none' ? '' : v }))}>
                <SelectTrigger data-testid="add-participant-trip">
                  <SelectValue placeholder="No trip — add to system only" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No trip — add to system only</SelectItem>
                  {trips.filter(t => t.status !== 'completed').map(t => (
                    <SelectItem key={t.trip_id} value={t.trip_id}>
                      {t.name} · {t.start_date}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-xs text-slate-600">
              <strong>What happens:</strong> They'll receive an email invitation to create their account.
              {addForm.trip_id ? ' Once set up, they\'ll be assigned to the selected trip.' : ' They\'ll appear as "Unassigned" until you add them to a trip.'}
              {addForm.role === 'admin' && ' They\'ll be granted admin access.'}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAddDialog(false); setAddForm({ name: '', email: '', trip_id: '', role: 'participant' }); }}>
              Cancel
            </Button>
            <Button
              className="bg-rojo hover:bg-rojo-dark"
              disabled={!addForm.name || !addForm.email || addSending}
              onClick={handleAddParticipant}
              data-testid="confirm-add-participant-btn"
            >
              <Send className="w-4 h-4 mr-2" />
              {addSending ? 'Sending...' : addForm.trip_id ? 'Send Invitation' : 'Add to System'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Full profile modal */}
      {viewingProfile && (
        <ParticipantProfileModal
          participant={viewingProfile}
          onClose={() => setViewingProfile(null)}
        />
      )}

      {/* ── Responsibilities Assignment Modal ── */}
      {respModalParticipant && (
        <Dialog open={true} onOpenChange={() => setRespModalParticipant(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Assign Responsibilities</DialogTitle>
            </DialogHeader>
            <div className="py-2">
              <p className="text-sm text-slate-500 mb-4">
                Select responsibilities for <strong>
                  {respModalParticipant.profile?.first_name || respModalParticipant.user?.name}
                </strong> on <strong>{respModalParticipant.trip?.name}</strong>.
              </p>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {respTypes.filter(r => r.active !== false).map(rt => (
                  <label key={rt.resp_id} className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-slate-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedResps.includes(rt.name)}
                      onChange={() => toggleResp(rt.name)}
                      className="w-4 h-4 accent-primary"
                      data-testid={`resp-${rt.resp_id}`}
                    />
                    <span className="text-sm text-slate-700">{rt.name}</span>
                  </label>
                ))}
              </div>
              {selectedResps.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {selectedResps.map(r => (
                    <span key={r} className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">{r}</span>
                  ))}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRespModalParticipant(null)}>Cancel</Button>
              <Button className="bg-primary" onClick={handleSaveResponsibilities} disabled={savingResps} data-testid="save-responsibilities-btn">
                {savingResps ? 'Saving...' : 'Save Responsibilities'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Batch Invite to Trip Dialog */}
      <Dialog open={showBatchInvite} onOpenChange={setShowBatchInvite}>
        <DialogContent className="max-w-lg max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-heading">Invite Participants to Trip</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 flex-1 overflow-hidden flex flex-col">
            {/* Trip selector */}
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1">Select Trip</label>
              <Select value={batchTripId} onValueChange={(v) => { setBatchTripId(v); setBatchSelected(new Set()); }}>
                <SelectTrigger data-testid="batch-trip-select"><SelectValue placeholder="Choose a trip..." /></SelectTrigger>
                <SelectContent>
                  {trips.filter(t => t.status !== 'completed').map(t => (
                    <SelectItem key={t.trip_id} value={t.trip_id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {batchTripId && (
              <>
                {/* Search */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search participants..."
                    value={batchSearch}
                    onChange={(e) => setBatchSearch(e.target.value)}
                    className="pl-9 h-9"
                    data-testid="batch-search"
                  />
                </div>

                {/* Eligible participants list */}
                <div className="flex-1 overflow-y-auto border rounded-lg divide-y max-h-[40vh]">
                  {getBatchEligible().length === 0 ? (
                    <p className="text-sm text-slate-400 text-center py-6">
                      {batchSearch ? 'No matching participants' : 'All participants are already assigned to this trip'}
                    </p>
                  ) : (
                    getBatchEligible().map(u => (
                      <label key={u.user_id} className="flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50 cursor-pointer">
                        <Checkbox
                          checked={batchSelected.has(u.user_id)}
                          onCheckedChange={(checked) => {
                            const next = new Set(batchSelected);
                            checked ? next.add(u.user_id) : next.delete(u.user_id);
                            setBatchSelected(next);
                          }}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-800 truncate">{u.name}</p>
                          <p className="text-xs text-slate-500 truncate">{u.email}</p>
                        </div>
                        {u.work_roles?.length > 0 && (
                          <span className="text-xs text-slate-400 truncate max-w-[120px]">{u.work_roles[0]}</span>
                        )}
                      </label>
                    ))
                  )}
                </div>

                {/* Select all / count */}
                <div className="flex items-center justify-between text-sm">
                  <button
                    className="text-primary hover:underline text-xs"
                    onClick={() => {
                      const eligible = getBatchEligible();
                      if (batchSelected.size === eligible.length) {
                        setBatchSelected(new Set());
                      } else {
                        setBatchSelected(new Set(eligible.map(u => u.user_id)));
                      }
                    }}
                  >
                    {batchSelected.size === getBatchEligible().length && getBatchEligible().length > 0 ? 'Deselect all' : 'Select all'}
                  </button>
                  <span className="text-slate-500 text-xs">{batchSelected.size} selected</span>
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBatchInvite(false)}>Cancel</Button>
            <Button
              className="bg-rojo hover:bg-rojo-dark"
              onClick={handleBatchInvite}
              disabled={batchSending || batchSelected.size === 0 || !batchTripId}
              data-testid="send-batch-invite-btn"
            >
              {batchSending ? 'Sending...' : `Send ${batchSelected.size} Invitation${batchSelected.size !== 1 ? 's' : ''}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* ── Guardian Link Dialog ── */}
      <Dialog open={!!guardianDialog} onOpenChange={(open) => !open && setGuardianDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {guardianDialog?.type === 'set-guardian' ? 'Set Guardian' : 'Add Dependent'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              {guardianDialog?.type === 'set-guardian'
                ? `Select the guardian for ${guardianDialog?.participant?.profile?.first_name || guardianDialog?.participant?.user?.name || 'this participant'}`
                : `Select the dependent (minor) for ${guardianDialog?.participant?.profile?.first_name || guardianDialog?.participant?.user?.name || 'this participant'}`}
            </p>
            <div className="max-h-60 overflow-y-auto space-y-1">
              {(() => {
                const seen = new Set();
                return allRoster
                  .filter(r => {
                    if (r.user_id === guardianDialog?.participant?.user_id) return false;
                    if (seen.has(r.user_id)) return false;
                    seen.add(r.user_id);
                    return true;
                  })
                  .map(r => {
                    const name = `${r.profile?.first_name || ''} ${r.profile?.last_name || ''}`.trim() || r.user?.name || r.user?.email;
                    return (
                      <button
                        key={r.user_id}
                        onClick={() => handleCreateGuardianLink(r.user_id)}
                        className="w-full flex items-center gap-3 p-2.5 rounded-lg hover:bg-slate-50 text-left transition-colors"
                        data-testid={`guardian-select-${r.user_id}`}
                      >
                        <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                          <User className="w-4 h-4 text-purple-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-slate-800">{name}</p>
                          <p className="text-xs text-slate-500">{r.user?.email}</p>
                        </div>
                      </button>
                    );
                  });
              })()}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Compose Email Modal ── */}
      <Dialog open={!!composeEmail} onOpenChange={(open) => !open && setComposeEmail(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">Send Email</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-slate-500">
              To: <span className="font-medium text-slate-800">{composeEmail?.name}</span> ({composeEmail?.to_email})
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-slate-700">Subject</label>
              <Input
                value={emailForm.subject}
                onChange={(e) => setEmailForm(f => ({ ...f, subject: e.target.value }))}
                placeholder="Subject line..."
                data-testid="compose-email-subject"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-slate-700">Message</label>
              <Textarea
                value={emailForm.message}
                onChange={(e) => setEmailForm(f => ({ ...f, message: e.target.value }))}
                placeholder="Type your message..."
                rows={5}
                data-testid="compose-email-message"
              />
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2 pt-2">
            <Button variant="outline" className="rounded-full" onClick={() => setComposeEmail(null)}>Cancel</Button>
            <Button
              className="rounded-full bg-rojo hover:bg-rojo-dark"
              disabled={sendingEmail || !emailForm.subject.trim() || !emailForm.message.trim()}
              onClick={handleSendEmail}
              data-testid="send-email-btn"
            >
              <Send className="w-4 h-4 mr-1.5" />
              {sendingEmail ? 'Sending...' : 'Send Email'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Group Email Modal ── */}
      <Dialog open={showGroupEmail} onOpenChange={(open) => !open && setShowGroupEmail(false)}>
        <DialogContent className="max-w-lg max-h-[90vh] flex flex-col overflow-hidden">
          <DialogHeader>
            <DialogTitle className="font-heading">Email Group</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 flex-1 min-h-0 overflow-y-auto pr-1">
            <div className="text-sm text-slate-500">
              To: <span className="font-medium text-slate-800">{selectedGroupRecipients.length} participant{selectedGroupRecipients.length !== 1 ? 's' : ''}</span>
            </div>
            <div className="max-h-20 overflow-y-auto bg-slate-50 rounded-lg p-2 flex flex-wrap gap-1" data-testid="group-email-recipients">
              {selectedGroupRecipients.map(r => (
                <span key={r.user_id} className="text-xs bg-white border border-slate-200 rounded-full px-2 py-0.5 text-slate-600">{r.name || r.email}</span>
              ))}
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-slate-700">
                CC <span className="text-xs text-slate-400 font-normal">optional — comma-separated admin/other emails</span>
              </label>
              {ccSuggestions.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-1.5" data-testid="cc-suggestions">
                  {ccSuggestions.map(u => {
                    const added = currentCcList.includes(u.email);
                    return (
                      <button
                        key={u.user_id}
                        type="button"
                        disabled={added}
                        onClick={() => addCcSuggestion(u.email)}
                        className={`text-xs rounded-full px-2.5 py-1 transition-colors ${added ? 'bg-primary/10 text-primary cursor-default' : 'bg-slate-100 hover:bg-primary/10 hover:text-primary text-slate-600'}`}
                        data-testid={`cc-suggestion-${u.user_id}`}
                      >
                        {added ? '✓ ' : '+ '}{u.name || u.email}
                      </button>
                    );
                  })}
                </div>
              )}
              <Input
                value={groupEmailForm.cc}
                onChange={(e) => setGroupEmailForm(f => ({ ...f, cc: e.target.value }))}
                placeholder="admin@guatemalasurgery.org, coordinator@..."
                data-testid="group-email-cc"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-slate-700">Subject</label>
              <Input
                value={groupEmailForm.subject}
                onChange={(e) => setGroupEmailForm(f => ({ ...f, subject: e.target.value }))}
                placeholder="Subject line..."
                data-testid="group-email-subject"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-slate-700">Message</label>
              <div className="group-email-quill" data-testid="group-email-message">
                <ReactQuill
                  theme="snow"
                  value={groupEmailForm.message}
                  onChange={(html) => setGroupEmailForm(f => ({ ...f, message: html }))}
                  modules={GROUP_EMAIL_QUILL_MODULES}
                  formats={GROUP_EMAIL_QUILL_FORMATS}
                  placeholder="Type your message… (paste from Word keeps Bold/Italic/Underline and lists)"
                />
              </div>
              <p className="text-[11px] text-slate-400">Bold, Italic, Underline, and lists are supported. Pasted colours, fonts, and links are stripped.</p>
            </div>
            <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              This sends as one email with everyone above (and any CC'd addresses) visible in the To/Cc fields, so replies continue as a group conversation in the email client.
            </p>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2 pt-2">
            <Button variant="outline" className="rounded-full" onClick={() => setShowGroupEmail(false)}>Cancel</Button>
            <Button
              className="rounded-full bg-rojo hover:bg-rojo-dark"
              disabled={sendingGroupEmail || !groupEmailForm.subject.trim() || !stripHtmlToText(groupEmailForm.message) || selectedGroupRecipients.length === 0}
              onClick={handleSendGroupEmail}
              data-testid="send-group-email-btn"
            >
              <Send className="w-4 h-4 mr-1.5" />
              {sendingGroupEmail ? 'Sending...' : `Send to Group (${selectedGroupRecipients.length})`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </PageLayout>
  );
};

export default ParticipantsManagement;
